import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Thermometer,
  Wind,
  Droplets,
  ChevronDown,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

import img from "../Images/hvac.webp";
import img1 from "../Images/chillers.jpg";
import img2 from "../Images/air.jpg";
import img3 from "../Images/humid.jpeg";
import img4 from "../Images/future.jpeg";
import img5 from "../Images/sustainable.png";
import img6 from "../Images/essentail.webp";

const HVAC = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const services = {
    chillers: {
      title: "Industrial Chillers",
      icon: <Thermometer className="w-6 h-6 sm:w-8 sm:h-8" />,
      description:
        "Advanced industrial chiller systems providing precise temperature control for manufacturing processes, data centers, and large-scale cooling applications across various industries.",
      features: [
        "Energy-efficient cooling solutions",
        "Precise temperature control (±0.5°C)",
        "Capacities from 5 to 500 tons",
        "Smart monitoring and control systems",
        "Low maintenance requirements",
        "24/7 operational capability"
      ],
      applications: [
        "Manufacturing facilities",
        "Data centers",
        "Chemical processing plants",
        "Food and beverage industry",
        "Pharmaceutical manufacturing",
        "Plastic injection molding"
      ],
      benefits: [
        "Reduced energy consumption",
        "Improved process efficiency",
        "Extended equipment lifespan",
        "Lower operational costs",
        "Enhanced product quality"
      ],
      image: img1,
    },
    airConditioning: {
      title: "Industrial Air Conditioning",
      icon: <Wind className="w-6 h-6 sm:w-8 sm:h-8" />,
      description:
        "Comprehensive industrial air conditioning solutions designed for large-scale facilities, warehouses, and specialized industrial environments requiring precise climate control.",
      features: [
        "Custom system design",
        "High-capacity cooling",
        "Advanced filtration systems",
        "Zone-based temperature control",
        "Industrial-grade components",
        "Remote monitoring capabilities"
      ],
      applications: [
        "Warehouses",
        "Production facilities",
        "Clean rooms",
        "Office complexes",
        "Research laboratories",
        "Industrial kitchens"
      ],
      benefits: [
        "Improved air quality",
        "Enhanced worker comfort",
        "Better equipment protection",
        "Increased productivity",
        "Regulatory compliance"
      ],
      image: img2,
    },
    humidifier: {
      title: "Industrial Humidification Systems",
      icon: <Droplets className="w-6 h-6 sm:w-8 sm:h-8" />,
      description:
        "State-of-the-art humidification systems providing precise moisture control for sensitive industrial processes, printing facilities, and environments requiring specific humidity levels.",
      features: [
        "Precise humidity control",
        "Multiple humidification methods",
        "Energy-efficient operation",
        "Automated moisture sensing",
        "Integrated control systems",
        "Scale prevention technology"
      ],
      applications: [
        "Printing facilities",
        "Textile manufacturing",
        "Wood processing",
        "Electronics manufacturing",
        "Museums and archives",
        "Agricultural facilities"
      ],
      benefits: [
        "Static electricity reduction",
        "Material integrity preservation",
        "Process optimization",
        "Quality control improvement",
        "Equipment protection"
      ],
      image: img3,
    },
  };

  // Structured data
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial HVAC Solutions",
    "description": "Comprehensive industrial HVAC solutions including chillers, air conditioning, and humidification systems",
    "numberOfItems": 3,
    "itemListElement": Object.entries(services).map(([key, service], index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": service.title,
        "description": service.description,
        "image": service.image
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "HVAC",
        "item": "https://www.gorthermenergy.com/products/hvac"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Industrial HVAC Systems | Chillers & Air Conditioning Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Premium industrial HVAC solutions including chillers, air conditioning & humidification systems. Expert installation in Nepal." />
        
        {/* Keywords */}
        <meta name="keywords" content="industrial HVAC, chillers, industrial air conditioning, humidification systems, climate control, cooling systems, HVAC Nepal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/products/hvac" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Industrial HVAC Solutions | Gortherm Energy" />
        <meta property="og:description" content="Complete industrial HVAC systems including chillers, air conditioning, and humidification for manufacturing facilities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/hvac" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial HVAC Systems" />
        <meta name="twitter:description" content="Industrial climate control solutions for manufacturing and process industries" />
        <meta name="twitter:image" content={img} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden"
          aria-label="Industrial HVAC solutions hero banner"
        >
          <img
            src={img}
            alt="Industrial HVAC systems including chillers, air conditioning, and climate control solutions for manufacturing facilities"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Industrial HVAC
                  <span className="text-sky-200"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed">
                  Engineered excellence in industrial climate control, process cooling, and environmental management systems across Nepal
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Main Services Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
          <header className="mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our HVAC Solutions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-4xl">
              Comprehensive industrial HVAC systems designed for manufacturing facilities, data centers, and specialized industrial environments requiring precise temperature and humidity control.
            </p>
          </header>

          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8"
          >
            {Object.entries(services).map(([key, service], idx) => (
              <motion.article
                key={key}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredCard(idx)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="relative h-40 sm:h-48">
                  <img
                    src={service.image}
                    alt={`${service.title} - ${service.description}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-4 sm:p-6">
                  <header className="flex items-center space-x-2 sm:space-x-3 mb-4">
                    <span className="text-blue-600" aria-hidden="true">{service.icon}</span>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {service.title}
                    </h3>
                  </header>
                  <p className="text-sm sm:text-base text-gray-600 mb-4">{service.description}</p>
                  <button
                    onClick={() =>
                      setExpandedSection(expandedSection === idx ? null : idx)
                    }
                    className="flex items-center space-x-2 text-sky-600 hover:text-sky-700 transition-colors text-sm sm:text-base"
                    aria-expanded={expandedSection === idx}
                  >
                    <span>Learn more</span>
                    <motion.div
                      animate={{ rotate: expandedSection === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === idx && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 space-y-4 sm:space-y-6"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Key Features
                          </h4>
                          <ul className="space-y-2">
                            {service.features.map((feature, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Applications
                          </h4>
                          <ul className="space-y-2">
                            {service.applications.map((app, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{app}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Benefits
                          </h4>
                          <ul className="space-y-2">
                            {service.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>

        {/* Industry Insights Section */}
        <section className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                Industry Insights & Best Practices
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Understanding modern HVAC technologies, sustainability, and maintenance best practices for industrial applications
              </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              {[
                {
                  title: "Smart HVAC: The Future of Climate Control",
                  image: img4,
                  content: "Smart HVAC systems integrate IoT sensors, machine learning, and automated controls to optimize performance and efficiency with energy savings up to 30%."
                },
                {
                  title: "Sustainable Cooling Solutions",
                  image: img5,
                  content: "Modern sustainable HVAC systems incorporate eco-friendly refrigerants, renewable energy sources, and heat recovery methods to minimize environmental impact."
                },
                {
                  title: "Essential HVAC Maintenance Guide",
                  image: img6,
                  content: "Regular maintenance is crucial for ensuring longevity and efficiency. Proper maintenance can extend equipment life by up to 40% and maintain peak operational efficiency."
                }
              ].map((insight, idx) => (
                <motion.article
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl overflow-hidden shadow-lg"
                >
                  <div className="h-40 sm:h-48 overflow-hidden">
                    <img
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      width="600"
                      height="300"
                    />
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                      {insight.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 text-justify">
                      {insight.content}
                    </p>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HVAC;